import React, { useState, useEffect, useRef } from 'react';
import { apiService } from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateTime } from '../../utils/dateUtils';
import '../shared.css';
import '../KG/Modal.css';

interface ViewPipelineRunsModalProps {
  kgId: string;
  versionId: string;
  orgId: string;
  onClose: () => void;
}

interface PipelineRun {
  id: string;
  name: string;
  status: string;
  created_at: number;
  last_updated_at: number;
}

const ACTIVE_STATUSES = new Set(['PENDING', 'RUNNING']);
const POLL_INTERVAL_MS = 10000;

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min < 60) return `${min}m ${sec}s`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return `${hr}h ${remMin}m`;
}

function getElapsed(run: PipelineRun): string {
  const endMs = ACTIVE_STATUSES.has(run.status) ? Date.now() : run.last_updated_at;
  return formatElapsed(endMs - run.created_at);
}

const STATUS_CLASSES: Record<string, string> = {
  PENDING: 'run-status-pending',
  RUNNING: 'run-status-running',
  COMPLETED: 'run-status-completed',
  ERROR: 'run-status-error',
  CANCELLED: 'run-status-cancelled',
};

const ViewPipelineRunsModal: React.FC<ViewPipelineRunsModalProps> = ({ kgId, versionId, orgId, onClose }) => {
  const batchName = `${kgId}_v${versionId}_elastic_etl`;
  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [batchId, setBatchId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = () => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const fetchRuns = async (resolvedBatchId: string) => {
    try {
      const result: any = await apiService.listRuns(resolvedBatchId, 5);
      const items: PipelineRun[] = (result?.data ?? []).map((r: any) => ({
        id: r.id,
        name: r.attributes?.name ?? r.id,
        status: r.attributes?.status ?? 'UNKNOWN',
        created_at: r.attributes?.created_at ?? 0,
        last_updated_at: r.attributes?.last_updated_at ?? r.attributes?.created_at ?? 0,
      }));
      setRuns(items);
      setNow(Date.now());
      setError('');
      return items;
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load runs'));
      return null;
    }
  };

  const load = async () => {
    setLoading(true);
    setError('');

    const pipelineId = apiService.getElasticEtlPipelineId();
    if (!pipelineId) {
      setError('No pipeline ID configured for the current environment.');
      setLoading(false);
      return;
    }

    try {
      let resolvedBatchId = batchId;
      if (!resolvedBatchId) {
        const searchResult: any = await apiService.searchBatches(pipelineId, orgId, batchName);
        const batches: any[] = searchResult?.data ?? [];
        const match = batches.find((b: any) => b.attributes?.name === batchName);
        if (!match) {
          setRuns([]);
          setLoading(false);
          return;
        }
        resolvedBatchId = match.id;
        setBatchId(resolvedBatchId);
      }

      const items = await fetchRuns(resolvedBatchId!);
      if (items && items.some(r => ACTIVE_STATUSES.has(r.status))) {
        startPolling(resolvedBatchId!);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load batch'));
    } finally {
      setLoading(false);
    }
  };

  const startPolling = (id: string) => {
    stopPolling();
    let consecutiveErrors = 0;
    pollRef.current = setInterval(async () => {
      const items = await fetchRuns(id);
      setNow(Date.now());
      if (!items) {
        consecutiveErrors++;
        if (consecutiveErrors >= 3) stopPolling(); // stop after 3 consecutive failures
        return;
      }
      consecutiveErrors = 0;
      if (!items.some(r => ACTIVE_STATUSES.has(r.status))) {
        stopPolling();
      }
    }, POLL_INTERVAL_MS);
  };

  const handleRefresh = async () => {
    if (!batchId) {
      load();
      return;
    }
    setLoading(true);
    setError('');
    const items = await fetchRuns(batchId);
    setLoading(false);
    if (items && items.some(r => ACTIVE_STATUSES.has(r.status))) {
      startPolling(batchId);
    }
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    load();
    return stopPolling;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Tick elapsed time for active runs every second
  useEffect(() => {
    if (!runs.some(r => ACTIVE_STATUSES.has(r.status))) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [runs]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Pipeline Runs</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="runs-modal-batch-name">
            Batch: <span>{batchName}</span>
          </div>

          {loading && <div className="page-loader"><div className="page-spinner" /></div>}
          {!loading && error && <div className="error-message">{error}</div>}

          {!loading && !error && runs.length === 0 && (
            <div className="empty-state">No runs found for this batch.</div>
          )}

          {!loading && runs.length > 0 && (
            <table className="runs-table">
              <thead>
                <tr>
                  <th>Run Name</th>
                  <th>Status</th>
                  <th>Started</th>
                  <th>Elapsed</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td className="runs-table-name" title={run.id}>{run.name}</td>
                    <td>
                      <span className={`run-status-badge ${STATUS_CLASSES[run.status] ?? ''}`}>
                        {run.status}
                      </span>
                    </td>
                    <td className="runs-table-time">{formatDateTime(run.created_at)}</td>
                    <td className="runs-table-elapsed">
                      {/* use now to re-render for active runs */}
                      {getElapsed({ ...run, last_updated_at: ACTIVE_STATUSES.has(run.status) ? now : run.last_updated_at })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="modal-footer">
          <button className="tab-btn tab-btn--ghost" onClick={handleRefresh} disabled={loading}>
            {loading ? <span className="inline-spinner" /> : 'Refresh'}
          </button>
          <button className="tab-btn tab-btn--primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default ViewPipelineRunsModal;
